/**
 * 업무 프로젝트(PMS) J7c2 — 프로젝트 설정 «자동화» 탭(규칙 카드 목록 ↔ 편집기).
 *
 * - 잔디 배치: 설정 본문 카드(880) 안에 규칙 카드(높이 50) 목록 · 카드를 누르면 화면 전체 층으로
 *   편집기(왼쪽 팔레트 · 가운데 캔버스 — J7c1 · 오른쪽 노드 설정 패널 — J7d · 위 «저장»)를 연다(J7c3).
 * - 편집·켜기·지우기는 서버가 준 can_manage 일 때만. 읽기 전용 안내는 설정 화면 공통 안내가 한다.
 * - 🔴 미완성 그래프(트리거 없음 등)도 화면에는 그대로 들고 있는다 — 저장 422 문구로 알린다(J7a2).
 *   422 의 `X-Automation-Node` 헤더가 가리키는 노드를 캔버스에 빨갛게 보이고 골라 둔다.
 * - 🔴 늦은 응답은 요청 번호로 버린다(목록·규칙·채널 모두) — A 를 열었다 닫고 B 를 열어도 A 가 B 를 덮지 않는다.
 * - 모바일은 목록(켜기·지우기)만 — 팔레트 끌어 놓기가 터치에서 안 되고 팔레트+패널이 폰 폭보다 넓다.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { taskPmAPI } from '../../api/client';
import { errorText } from './errorText';
import { confirmDialog, toast } from '../../components/AppDialog';
import useIsMobile from '../../hooks/useIsMobile';
import { COLORS, badge, btn, card, input } from '../../utils/uiStyles';
import AutomationCanvas from './automation/AutomationCanvas';
import AutomationNodePanel from './automation/AutomationNodePanel';
import { nodeSpec } from './automation/automationCatalog';

// J7a2 backend/routers/task_pm_automations.py STALE 와 같은 글자 — 이 문구일 때만 «다시 불러오기» 를 준다.
const STALE = '다른 사람이 먼저 규칙을 고쳤습니다. 새로 불러온 뒤 다시 저장해 주세요.';
const MOBILE_HINT = '자동화 그림은 PC 화면에서 편집할 수 있습니다.';
const NAME_MAX = 100;
const MINUTE = 60 * 1000;
const HAS_ZONE = /(?:Z|[+-]\d{2}:\d{2})$/i;

const emptyGraph = () => ({ nodes: [], edges: [] });
const snapshot = (name, graph) => JSON.stringify({ name, graph });
const asGraph = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return {
    ...value,
    nodes: Array.isArray(value.nodes) ? value.nodes : [],
    edges: Array.isArray(value.edges) ? value.edges : [],
  };
};

// 마지막 실행 — 백엔드 시각은 UTC naive 라 'Z' 를 붙여 읽는다(이미 Z·±hh:mm 이 있으면 그대로).
function ago(iso) {
  if (typeof iso !== 'string' || !iso) return '';
  const at = Date.parse(HAS_ZONE.test(iso) ? iso : `${iso}Z`);
  if (!Number.isFinite(at)) return '';
  const diff = Date.now() - at;
  if (diff < MINUTE) return '방금';           // 미래 시각(시계 어긋남)도 여기
  const minutes = Math.floor(diff / MINUTE);
  if (minutes < 60) return `${minutes}분 전`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}시간 전`;
  return `${Math.floor(hours / 24)}일 전`;
}

const alertBox = { ...card, padding: '8px 10px', background: COLORS.dangerSoft, color: COLORS.dangerText, marginBottom: '10px', fontSize: '12px' };
const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

export default function TaskPmAutomations({ project }) {
  const mobile = useIsMobile();
  const [items, setItems] = useState(null);           // null = 아직 못 받음
  const [canManage, setCanManage] = useState(false);
  const [listError, setListError] = useState('');
  const [pending, setPending] = useState({});         // 켜기 요청 중인 규칙 id
  const [editing, setEditing] = useState(null);       // null = 목록 · { id|null } = 편집기
  const [mobileHint, setMobileHint] = useState(false);
  const listSeq = useRef(0);

  const loadList = useCallback(async () => {
    const seq = ++listSeq.current;
    setListError('');
    try {
      const res = await taskPmAPI.listAutomations(project.id);
      if (seq !== listSeq.current) return;
      const data = res && res.data && typeof res.data === 'object' ? res.data : {};
      setItems(Array.isArray(data.items) ? data.items : []);
      setCanManage(data.can_manage === true);
    } catch (e) {
      if (seq !== listSeq.current) return;
      setListError(errorText(e, '자동화 규칙을 불러오지 못했습니다.'));
    }
  }, [project.id]);

  // 들어올 때 · 편집기에서 목록으로 돌아올 때 다시 받는다.
  useEffect(() => {
    if (editing === null) loadList();
  }, [editing, loadList]);
  // 화면을 떠나면 받고 있던 목록 응답을 버린다.
  useEffect(() => () => { listSeq.current += 1; }, []);

  const open = (id) => {
    if (mobile) { setMobileHint(true); return; }
    setMobileHint(false);
    setEditing({ id });
  };

  const toggle = async (rule) => {
    if (!canManage || pending[rule.id]) return;
    const wanted = !rule.enabled;
    setPending((p) => ({ ...p, [rule.id]: true }));
    setListError('');
    try {
      const res = await taskPmAPI.setAutomationEnabled(rule.id, wanted);
      const brief = res && res.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : null;
      setItems((list) => (list || []).map((r) => (r.id === rule.id
        ? (brief ? { ...r, ...brief } : { ...r, enabled: wanted })
        : r)));
    } catch (e) {
      setListError(errorText(e, '규칙을 켜거나 끄지 못했습니다.'));
    } finally {
      setPending((p) => {
        const next = { ...p };
        delete next[rule.id];
        return next;
      });
    }
  };

  const remove = async (rule) => {
    if (!canManage) return;
    if (!(await confirmDialog('규칙을 지울까요? 실행 기록도 함께 지워집니다.'))) return;
    setListError('');
    try {
      await taskPmAPI.deleteAutomation(rule.id);
      setItems((list) => (list || []).filter((r) => r.id !== rule.id));
      toast('규칙을 지웠습니다.');
    } catch (e) {
      setListError(errorText(e, '규칙을 지우지 못했습니다.'));
    }
  };

  if (editing !== null && !mobile) {
    return (
      <RuleEditor
        project={project}
        initialId={editing.id}
        canManage={canManage}
        onBack={() => setEditing(null)}
      />
    );
  }

  const renderCard = (rule) => {
    const enabled = Boolean(rule.enabled);
    const busy = Boolean(pending[rule.id]);
    const switchOff = !canManage || busy;
    const when = rule.last_run_at ? ago(rule.last_run_at) : '실행 전';
    const meta = [when, `실행 ${Number(rule.run_count) || 0}회`].filter(Boolean).join(' · ');
    return (
      <li key={rule.id} style={{
        ...card, height: '50px', width: '100%', boxSizing: 'border-box', padding: '0 14px', marginBottom: '8px',
        display: 'flex', alignItems: 'center', gap: '12px',
      }}>
        <button type="button" onClick={() => open(rule.id)}
          style={{
            ...ellipsis, flex: 1, minWidth: 0, padding: 0, border: 'none', background: 'none', textAlign: 'left',
            fontSize: '14px', fontWeight: 700, color: COLORS.text, cursor: 'pointer',
          }}>
          {rule.name}
        </button>
        <span style={{ fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap' }}>
          {nodeSpec(rule.trigger_type)?.label || ''}
        </span>
        <span style={{ fontSize: '12px', color: COLORS.textMuted, whiteSpace: 'nowrap' }}>{meta}</span>
        {rule.last_error && <span style={badge('warn')} title={rule.last_error}>최근 실패</span>}
        <button type="button" role="switch" aria-checked={enabled} aria-label={`${rule.name} 켜기`} disabled={switchOff}
          onClick={() => toggle(rule)}
          style={{
            position: 'relative', flex: '0 0 36px', width: '36px', height: '20px', padding: 0, border: 'none',
            borderRadius: '999px', background: enabled ? COLORS.primary : COLORS.inputBorder,
            cursor: switchOff ? 'not-allowed' : 'pointer', opacity: switchOff ? 0.5 : 1,
          }}>
          <span aria-hidden="true" style={{
            position: 'absolute', top: '2px', left: enabled ? '18px' : '2px', width: '16px', height: '16px',
            borderRadius: '50%', background: COLORS.surface, transition: 'left .15s',
          }} />
        </button>
        {canManage && (
          <button type="button" aria-label={`${rule.name} 지우기`} onClick={() => remove(rule)}
            style={{ border: 'none', background: 'none', padding: '0 2px', fontSize: '14px', color: COLORS.textSub, cursor: 'pointer' }}>
            ✕
          </button>
        )}
      </li>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 700 }}>자동화</h2>
          <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.textSub }}>
            업무가 바뀔 때 정해 둔 일을 자동으로 합니다.
          </p>
        </div>
        {canManage && (
          <button type="button" onClick={() => open(null)} style={btn('primary', 'sm')}>새 규칙</button>
        )}
      </div>

      {(mobileHint || editing !== null) && (
        <p style={{ margin: '0 0 10px', fontSize: '12px', color: COLORS.textSub }}>{MOBILE_HINT}</p>
      )}
      {listError && <div role="alert" style={alertBox}>{listError}</div>}

      {items === null && !listError && (
        <p style={{ margin: 0, fontSize: '13px', color: COLORS.textSub }}>불러오는 중…</p>
      )}
      {items !== null && items.length === 0 && (
        <p style={{ margin: 0, fontSize: '13px', color: COLORS.textSub }}>아직 규칙이 없습니다.</p>
      )}
      {items !== null && items.length > 0 && (
        <ul aria-label="자동화 규칙" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {items.map(renderCard)}
        </ul>
      )}
    </div>
  );
}

function RuleEditor({ project, initialId, canManage, onBack }) {
  const [ruleId, setRuleId] = useState(initialId);
  const [loading, setLoading] = useState(initialId != null);
  const [loadError, setLoadError] = useState('');
  const [name, setName] = useState('');
  const [graph, setGraph] = useState(emptyGraph);
  const [version, setVersion] = useState(null);
  const [baseline, setBaseline] = useState(() => snapshot('', emptyGraph()));
  const [selectedId, setSelectedId] = useState(null);
  const [errorNodeId, setErrorNodeId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [stale, setStale] = useState(false);
  const [drawKey, setDrawKey] = useState(0);           // 다시 불러오면 캔버스를 새로 그려 전체 맞춤을 다시 한다
  const [channels, setChannels] = useState(null);      // null = 안 받음(또는 못 받음)
  const [channelsEnabled, setChannelsEnabled] = useState(null);   // null = 모름
  const [channelsError, setChannelsError] = useState('');
  const ruleSeq = useRef(0);
  const channelSeq = useRef(0);
  const rootRef = useRef(null);

  // 층이 열리면 초점을 가져온다(Esc·Tab 을 층 루트에서 받으려면 초점이 안에 있어야 한다).
  useEffect(() => { rootRef.current?.focus(); }, []);
  // 🔴 렌더 뒤 초점이 body 로 빠져 있으면(저장 중 잠긴 «저장»·사라진 «다시 불러오기») 층으로 되찾는다.
  //    확인 창·전역 검색에 초점이 있으면 body 가 아니므로 뺏지 않는다.
  useEffect(() => {
    if (document.activeElement === document.body) rootRef.current?.focus();
  });

  // 서버 상세로 이름·그림·버전·dirty 기준을 바꾼다. 응답이 이상하면 fallback(지금 값)을 쓴다.
  const applyDetail = (data, fallback) => {
    const d = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
    const nextName = typeof d.name === 'string' ? d.name : fallback.name;
    const nextGraph = asGraph(d.graph) || fallback.graph;
    setName(nextName);
    setGraph(nextGraph);
    if (d.version != null) setVersion(d.version);
    if (d.id != null) setRuleId(d.id);
    setBaseline(snapshot(nextName, nextGraph));
  };

  // 🔴 저장 응답은 보낼 때 그대로인 칸(sentName·sentGraph)만 서버 값으로 바꾼다 — 저장 중에 고친 이름·그림을
  //    늦게 온 응답이 덮지 않게. 기준선은 서버 값이라 그사이 고친 칸은 dirty 로 남아 떠날 때 확인한다.
  //    그림 편집은 늘 새 객체를 만든다(캔버스·패널 모두) — 같은 객체면 안 고친 것이다.
  const applySaved = (data, fallback, sentName, sentGraph) => {
    const d = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
    const nextName = typeof d.name === 'string' ? d.name : fallback.name;
    const nextGraph = asGraph(d.graph) || fallback.graph;
    setName((prev) => (prev === sentName ? nextName : prev));
    setGraph((prev) => (prev === sentGraph ? nextGraph : prev));
    if (d.version != null) setVersion(d.version);
    if (d.id != null) setRuleId(d.id);
    setBaseline(snapshot(nextName, nextGraph));
  };

  useEffect(() => {
    const seq = ++ruleSeq.current;
    if (initialId != null) {
      taskPmAPI.getAutomation(initialId)
        .then((res) => {
          if (seq !== ruleSeq.current) return;
          applyDetail(res?.data, { name: '', graph: emptyGraph() });
          setLoading(false);
        })
        .catch((e) => {
          if (seq !== ruleSeq.current) return;
          setLoadError(errorText(e, '규칙을 불러오지 못했습니다.'));
          setLoading(false);
        });
    }
    return () => { ruleSeq.current += 1; };
  }, [initialId]);

  // 채널 목록은 담당자만 받는다(J7a2 채널 API 는 manage 전용).
  useEffect(() => {
    if (!canManage) return undefined;
    const seq = ++channelSeq.current;
    taskPmAPI.automationChannels(project.id)
      .then((res) => {
        if (seq !== channelSeq.current) return;
        const data = res && res.data && typeof res.data === 'object' ? res.data : {};
        setChannels(Array.isArray(data.items) ? data.items : []);
        setChannelsEnabled(data.enabled !== false);
      })
      .catch((e) => {
        if (seq !== channelSeq.current) return;
        setChannelsError(errorText(e, '메신저 채널 목록을 가져오지 못했습니다.'));
      });
    return () => { channelSeq.current += 1; };
  }, [canManage, project.id]);

  const nodes = graph.nodes;
  const selectedNode = selectedId != null ? nodes.find((n) => n.id === selectedId) || null : null;
  const triggerType = nodes.find((n) => nodeSpec(n.type)?.group === 'trigger')?.type ?? null;

  // 메신저 노드 요약 — 채널 배열을 받았고 봇이 켜져 있을 때만(아니면 캔버스는 «채널 지정됨»).
  const summaryOverride = useMemo(() => {
    if (!Array.isArray(channels) || channelsEnabled === false) return undefined;
    const out = {};
    nodes.forEach((n) => {
      if (!n || n.type !== 'action_messenger_post') return;
      const value = n.data && n.data.channel_id ? String(n.data.channel_id) : '';
      if (!value) return;
      const channel = channels.find((c) => c && c.id != null && String(c.id) === value);
      if (!channel) { out[n.id] = '(없는 채널)'; return; }
      const channelName = channel.name || String(channel.id);
      out[n.id] = channel.team ? `${channel.team} · ${channelName}` : channelName;
    });
    return out;
  }, [channels, channelsEnabled, nodes]);

  const dirty = !loading && !loadError && snapshot(name, graph) !== baseline;
  const canSave = canManage && !loading && !loadError && !saving && Boolean(name.trim());

  const back = async () => {
    if (dirty && !(await confirmDialog('저장하지 않은 변경이 사라집니다. 목록으로 갈까요?'))) return;
    onBack();
  };

  // 🔴 Esc·Tab 은 층 루트의 onKeyDown 에서만 받는다 — window 리스너면 확인 창의 Esc 가 층까지 닫는다.
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (!e.defaultPrevented) back();
      return;
    }
    if (e.key !== 'Tab') return;
    const root = rootRef.current;
    if (!root) return;
    const items = Array.from(root.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )).filter((el) => !el.disabled);
    if (!items.length) { e.preventDefault(); return; }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === root)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const changeNodeData = (data) => {
    if (selectedId == null) return;
    setGraph((g) => ({ ...g, nodes: g.nodes.map((n) => (n.id === selectedId ? { ...n, data } : n)) }));
  };

  const save = async () => {
    if (!canSave) return;
    const sentName = name;
    const sentGraph = graph;
    const body = { name: name.trim(), graph };
    setSaving(true);
    setSaveError('');
    setStale(false);
    try {
      const res = ruleId == null
        ? await taskPmAPI.createAutomation(project.id, body)
        : await taskPmAPI.updateAutomation(ruleId, { ...body, version });
      applySaved(res?.data, body, sentName, sentGraph);
      setErrorNodeId(null);
      toast('규칙을 저장했습니다.');
    } catch (e) {
      const status = e?.response?.status;
      setSaveError(errorText(e, '저장하지 못했습니다.'));
      if (status === 422) {
        // 같은 오리진이라 헤더를 읽을 수 있다 — axios 는 키를 소문자로 준다.
        const nodeId = e?.response?.headers?.['x-automation-node'] || null;
        setErrorNodeId(nodeId);
        if (nodeId && graph.nodes.some((n) => n.id === nodeId)) setSelectedId(nodeId);
      } else if (status === 409 && ruleId != null && e?.response?.data?.detail === STALE) {
        setStale(true);
      }
    } finally {
      setSaving(false);
    }
  };

  const reload = async () => {
    if (ruleId == null) return;
    const seq = ++ruleSeq.current;
    try {
      const res = await taskPmAPI.getAutomation(ruleId);
      if (seq !== ruleSeq.current) return;
      applyDetail(res?.data, { name, graph });
      setErrorNodeId(null);
      setSelectedId(null);
      setStale(false);
      setSaveError('');
      setDrawKey((k) => k + 1);
    } catch (e) {
      if (seq !== ruleSeq.current) return;
      setSaveError(errorText(e, '규칙을 불러오지 못했습니다.'));
    }
  };

  // 화면 전체 층(J7c3) — 서랍·노트 패널(60)·사이드바를 덮고 알림 센터(80)·확인 창·토스트보다는 아래.
  // body 는 잠그지 않는다(스크롤 주인은 TaskPmPage 의 <main>) — overscrollBehavior 로 뒤 화면에 넘기지 않는다.
  return (
    <div ref={rootRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="자동화 규칙 편집" onKeyDown={handleKeyDown}
      style={{
        position: 'fixed', inset: 0, zIndex: 70, display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
        padding: '16px 24px', background: COLORS.bg, overflow: 'auto', overscrollBehavior: 'contain', outline: 'none',
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '40px', marginBottom: '12px', flex: '0 0 auto' }}>
        <button type="button" onClick={back} style={btn('secondary', 'sm')}>← 목록</button>
        {!loadError && (
          <input aria-label="규칙 이름" value={name} maxLength={NAME_MAX} placeholder="규칙 이름"
            disabled={!canManage || loading} onChange={(e) => setName(e.target.value)}
            style={{ ...input, flex: 1, minWidth: 0, padding: '6px 10px', fontSize: '13px' }} />
        )}
        {!loadError && canManage && (
          <button type="button" onClick={save} disabled={!canSave}
            style={{ ...btn('primary', 'sm'), opacity: canSave ? 1 : 0.5 }}>저장</button>
        )}
      </div>
      {/* 층이 설정 화면 공통 안내를 가린다 — 멤버·종료된 프로젝트 둘 다에 맞는 한 문장. */}
      {!canManage && (
        <p style={{ margin: '0 0 8px', fontSize: '12px', color: COLORS.textSub }}>보기만 할 수 있습니다.</p>
      )}

      {loadError && <div role="alert" style={alertBox}>{loadError}</div>}
      {!loadError && saveError && (
        <div role="alert" style={alertBox}>
          <div>{saveError}</div>
          {stale && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
              <span style={{ flex: 1, fontSize: '12px', color: COLORS.textSub }}>다시 불러오면 지금 편집한 것은 사라집니다.</span>
              <button type="button" onClick={reload} style={btn('secondary', 'sm')}>다시 불러오기</button>
            </div>
          )}
        </div>
      )}
      {!loadError && channelsError && (
        <div style={{ fontSize: '12px', color: COLORS.warn, marginBottom: '8px' }}>{channelsError}</div>
      )}

      {!loadError && loading && (
        <p style={{ margin: 0, fontSize: '13px', color: COLORS.textSub }}>불러오는 중…</p>
      )}
      {/* 창이 낮아 머리 + 알림 + 360 을 못 담으면 층이 세로로 스크롤된다. */}
      {!loadError && !loading && (
        <div style={{ display: 'flex', flex: '1 1 auto', minHeight: '360px' }}>
          <div key={drawKey} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <AutomationCanvas
              graph={graph}
              onChange={setGraph}
              selectedId={selectedId}
              onSelect={setSelectedId}
              readOnly={!canManage}
              errorNodeId={errorNodeId}
              summaryOverride={summaryOverride}
              height="100%"
            />
          </div>
          <div style={{ flex: '0 0 auto' }}>
            <AutomationNodePanel
              node={selectedNode}
              triggerType={triggerType}
              options={project.options || {}}
              members={project.members || []}
              channels={channels}
              channelsEnabled={channelsEnabled}
              readOnly={!canManage}
              onChange={changeNodeData}
            />
          </div>
        </div>
      )}
    </div>
  );
}
